import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";
import { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import { useBuildingStorageQuota } from "@/hooks/buildings/useBuildingStorageQuota";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { BottomRightLaneSlot } from "@/components/layout/BottomRightLane";
import { FranklinSpeechBubble } from "@/components/chat/SidebarChat/FranklinSpeechBubble";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  BuildingStorageQuotaError,
  buildingStorageQuotaKey,
  formatStorageBytes,
  getBuildingStorageQuota,
} from "../services/buildingStorageQuota";
import {
  feedbackSession,
  observeFeedback,
  completedBatches,
  refreshBatches,
  type FeedbackFile,
} from "../utils/storageFeedbackSession";
import { storageCopy, storageState } from "../utils/storagePresentation";
import { StorageActions } from "./StorageActions";

interface FeedbackRefreshOptions {
  key: string;
  orgId: string | undefined;
  userId: string | number | undefined;
  buildingId: string;
  quota: ReturnType<typeof useBuildingStorageQuota>;
  files: FeedbackFile[];
  client: ReturnType<typeof useQueryClient>;
  revision: number;
  activeKey: { current: string };
  setRevision: (updater: (value: number) => number) => void;
}
function useFeedbackRefresh(options: FeedbackRefreshOptions) {
  const { key, orgId, userId, buildingId, quota, files, client, revision, activeKey, setRevision } =
    options;
  useEffect(() => {
    if (!orgId || !userId) return;
    const session = feedbackSession(key, quota.data, files.length > 0);
    if (!quota.data || quota.error instanceof BuildingStorageQuotaError) return;
    observeFeedback(session, quota.data);
    if (session.busy) return;
    const batches = completedBatches(session, files);
    if (!batches.length) return;
    session.busy = true;
    void refreshBatches(session, batches, () =>
      client.fetchQuery({
        queryKey: buildingStorageQuotaKey(buildingId, orgId),
        queryFn: () => getBuildingStorageQuota(buildingId),
        staleTime: 0,
      })
    )
      .then(() => {
        if (activeKey.current === key) setRevision((value) => value + 1);
      })
      .catch(() => undefined);
  }, [
    key,
    orgId,
    userId,
    buildingId,
    quota.data,
    quota.error,
    files,
    client,
    revision,
    activeKey,
    setRevision,
  ]);
}
function useStorageFeedback(buildingId: string) {
  const { currentOrganization, isReady } = useOrganizationContext();
  const { userProfile } = useAuth();
  const orgId = isReady ? currentOrganization?.id : undefined;
  const quota = useBuildingStorageQuota(buildingId, orgId);
  const store = useUploadQueueStore();
  const client = useQueryClient();
  const key = `${userProfile?.id}:${orgId}:${buildingId}`;
  const [revision, setRevision] = useState(0);
  const activeKey = useRef(key);
  useLayoutEffect(() => {
    activeKey.current = key;
  }, [key]);
  const files: FeedbackFile[] = Array.from(store.storageBatchFiles?.values() ?? store.queue)
    .filter((file) => file.buildingId === buildingId && file.orgId === orgId && file.batchId)
    .map((file) => ({
      batchId: file.batchId,
      status: file.status,
      documentCreated: file.documentCreated,
      registrationUncertain: file.registrationUncertain,
    }));
  useFeedbackRefresh({
    key,
    orgId,
    userId: userProfile?.id,
    buildingId,
    quota,
    files,
    client,
    revision,
    activeKey,
    setRevision,
  });
  const session = feedbackSession(key, quota.data, files.length > 0);
  const clearNotice = () => {
    session.notice = null;
    setRevision((value) => value + 1);
  };
  return { session, quota, orgId, userId: userProfile?.id, revision, clearNotice };
}
function canShowNotice(result: ReturnType<typeof useStorageFeedback>) {
  if (!result.session.notice || !result.orgId || !result.userId) return false;
  if (result.quota.error instanceof BuildingStorageQuotaError) return false;
  if (result.quota.data?.activationPending) return false;
  return !result.quota.data || storageState(result.quota.data) !== "unlimited";
}
export const StorageUploadFeedback = observer(function StorageUploadFeedback({
  buildingId,
}: {
  buildingId: string;
}) {
  const result = useStorageFeedback(buildingId);
  const { language } = useLanguage();
  const words = storageCopy[language];
  const { session, revision, clearNotice } = result;
  const notice = session.notice;
  if (!canShowNotice(result) || !notice) return null;
  return (
    <BottomRightLaneSlot name="storageWarning">
      <FranklinSpeechBubble
        role="status"
        aria-live="polite"
        data-testid="storage-upload-bubble"
        data-batch-id={notice.batchId}
        data-revision={revision}
      >
        <div className="mb-3 flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm">
            {(notice.kind === "full" ? words.fullBubble : words.nearBubble).replace(
              "{remaining}",
              formatStorageBytes(notice.remaining)
            )}
          </p>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={words.dismiss}
            onClick={() => {
              clearNotice();
            }}
          >
            <X className="size-4" />
          </Button>
        </div>
        <StorageActions buildingId={buildingId} actionId="get_more_space_bubble" />
      </FranklinSpeechBubble>
    </BottomRightLaneSlot>
  );
});
